<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modalTitle"
    :subtitle="delegation.description"
    @on-confirm="saveItem"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
  >
    <v-tabs centered v-model="selectedTab">
      <v-tab>{{ $t("Description") }}</v-tab>
      <v-tab>{{ $t("Users") }}</v-tab>
      <v-tab-item>
        <v-card flat class="my-0">
          <v-card-text>
            <v-form class="mt-5">
              <v-text-field
                v-model="delegation.description"
                :error-messages="descriptionErrors"
                v-bind:label="$t('Description')"
                @input="$v.delegation.description.$touch()"
                @blur="$v.delegation.description.$touch()"
              ></v-text-field>

              <v-select
                v-model="delegation.type"
                @change="delegationTypeChanged"
                item-text="text"
                item-value="id"
                v-bind:label="$t('Type')"
                :items="types"
              ></v-select>

              <v-row v-if="isScheduled">
                <v-col cols="12" sm="6" md="6">
                  <BaseDatePicker
                    v-model="delegation.startDate"
                    :label="$t('StartDate')"
                    :error-messages="startDateErrors"
                    @input="$v.delegation.startDate.$touch()"
                    @blur="$v.delegation.startDate.$touch()"
                  />
                </v-col>

                <v-col cols="12" sm="6" md="6">
                  <BaseDatePicker
                    v-model="delegation.endDate"
                    :label="$t('EndDate')"
                    :error-messages="endDateErrors"
                    @input="$v.delegation.endDate.$touch()"
                    @blur="$v.delegation.endDate.$touch()"
                  />
                </v-col>
              </v-row>
              <v-row class="pa-0 ma-0">
                <v-col class="pa-0 ma-0" cols="12" sm="6" md="6">
                  <v-checkbox
                    v-model="delegation.delegateContentTypesRights"
                    v-bind:label="$t('ContentTypeAccessRights')"
                    :error-messages="checkboxErrors"
                    @change="delegateContentTypesRightsChanged"
                  ></v-checkbox>
                </v-col>
                <v-col class="pa-0 ma-0" cols="12" sm="6" md="6">
                  <v-checkbox
                    v-model="delegation.delegateUserSearches"
                    v-bind:label="$t('SavedSearches')"
                    :error-messages="checkboxErrors"
                  ></v-checkbox>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card>
          <v-card-text>
            <v-select
              v-bind:label="$t('DelegatedBy')"
              v-model="delegation.delegatorUser"
              :error-messages="delegatorUserErrors"
              required
              @change="$v.delegation.delegatorUser.$touch()"
              @blur="$v.delegation.delegatorUser.$touch()"
              :items="userList"
              item-text="text"
              item-value="id"
              :return-object="true"
            ></v-select>
            <v-row>
              <BaseDualList
                :source="availablesUsers"
                :destination="delegation.users"
                item-text="text"
                item-id="id"
                @onChangeList="onUserListChange"
              />
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs>
  </base-modal>
</template>
<script>
import { required, requiredIf } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";

import Utils from "@/ecm/common/Utils.js";

import DelegationService from "@/services/DelegationService";
import RoleService from "@/services/RoleService";
import Delegation from "@/ecm/models/Delegation";
import DelegationType from "@/ecm/variables/DelegationType";

export default {
  created() {
    RoleService.getUserList(0).then((data) => {
      this.userList = data;
    });
    this.$v.$reset();
  },
  data() {
    return {
      userList: [],
      availablesUsers: [],
      types: [
        { id: DelegationType.Manual, text: this.$t("Manual") },
        { id: DelegationType.Scheduled, text: this.$t("Scheduled") },
      ],
      errorMessage: null,
      modal: {
        title: this.$t("Delegation"),
        key: "delegationModal",
      },
      open: false,
      delegation: new Delegation(),
      selectedTab: 0,
      isNew: true,
    };
  },
  mixins: [validationMixin],
  validations: {
    delegation: {
      description: { required },
      delegatorUser: { required },
      startDate: {
        required: requiredIf(function () {
          return this.isScheduled;
        }),
      },
      endDate: {
        required: requiredIf(function () {
          return this.isScheduled;
        }),
      },
    },
  },
  computed: {
    modalInstance() {
      return this.$refs[this.modal.key];
    },
    modalTitle() {
      return (
        this.modal.title +
        (this.delegation.description ? " - " + this.delegation.description : "")
      );
    },
    isScheduled() {
      return this.delegation.type == DelegationType.Scheduled;
    },
    startDateErrors() {
      const errors = [];
      if (!this.$v.delegation.startDate.$dirty) return errors;
      !this.$v.delegation.startDate.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    endDateErrors() {
      const errors = [];
      if (!this.$v.delegation.endDate.$dirty) return errors;
      !this.$v.delegation.endDate.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    descriptionErrors() {
      const errors = [];
      if (!this.$v.delegation.description.$dirty) return errors;
      !this.$v.delegation.description.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    delegatorUserErrors() {
      const errors = [];
      if (!this.$v.delegation.delegatorUser.$dirty) return errors;
      !this.$v.delegation.delegatorUser.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    checkboxErrors() {
      const errors = [];
      !this.delegation.delegateUserSearches &&
        !this.delegation.delegateContentTypesRights &&
        errors.push(this.$t("ItemsSelectionError"));
      return errors;
    },
  },
  methods: {
    delegateContentTypesRightsChanged(val) {
      if (!val) {
        this.delegation.delegateBaskets = false;
        this.delegation.delegateFlows = false;
      }
    },
    delegationTypeChanged() {
      if (!this.isScheduled) {
        this.delegation.startDate = "";
        this.delegation.endDate = "";
      }
    },
    onUserListChange: function ({ destination, source }) {
      this.availablesUsers = source;
      this.delegation.users = destination;
    },
    saveItem() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        if (this.$v.delegation.delegatorUser.$invalid) {
          this.selectedTab = 1;
        } else {
          this.selectedTab = 0;
        }
        return;
      }
      if (this.checkboxErrors.length > 0) {
        this.selectedTab = 0;
        return;
      }
      if (!this.IsValid()) {
        this.selectedTab = 1;
        return;
      }

      this.modalInstance.progress(true);

      DelegationService.save(this.delegation.toDto())
        .then((result) => {
          if (result) {
            if (this.isNew) {
              Utils.toast.success(
                this.$t("Delegation") + " " + this.$t("CreatedSuccessfullyFem")
              );
            } else {
              Utils.toast.success(
                this.$t("Delegation") + " " + this.$t("UpdatedSuccessfullyFem")
              );
            }
            this.hide();
            this.$emit("on-item-saved");
          }
        })
        .catch((error) => {
          // error
        })
        .then(() => {
          //always
          this.modalInstance.progress(false);
        });
    },
    IsValid() {
      if (this.delegation.users.length == 0) {
        Utils.toast.error(this.$t("ItemsSelectionError"));
        return false;
      }
      return true;
    },
    new() {
      this.delegation = new Delegation();
      this.availablesUsers = this.userList;
      this.$v.$reset();
      this.show();
    },
    edit(dto) {
      this.delegation = new Delegation(dto);
      this.isNew = false;

      var availablesUsers = [...this.userList];

      for (let i = 0; i < this.delegation.users.length; i++) {
        const selected = this.userList.find(
          (user) => user.id == this.delegation.users[i].id
        );

        if (selected) {
          this.delegation.users[i].text = selected.text;

          availablesUsers = availablesUsers.filter((x) => {
            return x.id != selected.id;
          });
        }
      }
      this.availablesUsers = availablesUsers;
      this.show();
    },
    show() {
      this.open = true;
      this.selectedTab = 0;
    },
    hide() {
      this.open = false;
      this.selectedTab = 0;
      this.isNew = true;
    },
  },
};
</script>
